import { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() =>
        localStorage.getItem("hoshizora_token") ? null : false,
    );
    const [loading, setLoading] = useState(() =>
        Boolean(localStorage.getItem("hoshizora_token")),
    );
    const didInit = useRef(false);

    useEffect(() => {
        if (didInit.current) return;
        didInit.current = true;
        const token = localStorage.getItem("hoshizora_token");
        if (!token) return;
        (async () => {
            try {
                const { data } = await api.get("/auth/me");
                setUser(data);
                localStorage.setItem("hoshizora_pos_user", JSON.stringify(data));
                // Non-owners: pin the primary brand so first fetches are scoped.
                if (data?.role !== "owner" && data?.brand) {
                    const accessible = data.brands && data.brands.length ? data.brands : [data.brand];
                    const stored = localStorage.getItem("hoshizora_active_brand");
                    if (!stored || !accessible.includes(stored)) {
                        localStorage.setItem("hoshizora_active_brand", data.brand);
                    }
                }
            } catch (e) {
                // Offline POS only: a network failure (no HTTP response) keeps the last verified profile. 401/403 still sign out.
                const cached = localStorage.getItem("hoshizora_pos_user");
                if (!e.response && cached && window.location.pathname.startsWith("/pos")) {
                    setUser(JSON.parse(cached));
                    return;
                }
                localStorage.removeItem("hoshizora_token");
                setUser(false);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.token) localStorage.setItem("hoshizora_token", data.token);
        setUser(data.user);
        localStorage.setItem("hoshizora_pos_user", JSON.stringify(data.user));
        setLoading(false);
        // Non-owners: pin brand immediately so the first catalog fetch is scoped.
        if (data.user?.role !== "owner" && data.user?.brand) {
            const accessible = data.user.brands && data.user.brands.length ? data.user.brands : [data.user.brand];
            const stored = localStorage.getItem("hoshizora_active_brand");
            if (!stored || !accessible.includes(stored)) {
                localStorage.setItem("hoshizora_active_brand", data.user.brand);
            }
        }
        return data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        localStorage.removeItem("hoshizora_token");
        localStorage.removeItem("hoshizora_active_brand");
        localStorage.removeItem("hoshizora_pos_user");
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}
